import { darkGrey, selfDrawnHikingColor, stopOverColor, tourColor } from "./common";
import { detourClosedWay, detourLine, detourStartEndPoint } from "./detour";
import {
  alpineHikeIcon,
  hikeIcon,
  hikingActivityClusterIcon,
  hikingActivityIcon,
  hikingClusterIcon,
  hikingIcon,
  mountainHikeIcon,
  poiClusterIcon,
  poiFireplaceIcon,
  poiHotelIcon,
  poiRestaurantIcon,
  poiVideoIcon,
  schooltripHikeIcon,
  snowShoeHikeIcon,
  winterHikeIcon,
} from "./icons";

/**
 * Stop Over Style
 */

const controlPointBackground = {
  "circle-radius": 13,
  "circle-fill-color": "#fff",
};

const stopOverCircle = {
  "circle-radius": 12,
  "circle-fill-color": ["case", ["==", ["get", "type"], "hiking"], selfDrawnHikingColor, stopOverColor],
};

const stopOver = [controlPointBackground, stopOverCircle];

const firstStopOverCircle1 = {
  "circle-radius": 6,
  "circle-fill-color": ["case", ["==", ["get", "type"], "hiking"], selfDrawnHikingColor, stopOverColor],
};

const firstStopOverCircle2 = {
  "circle-radius": 9,
  "circle-fill-color": "#fff",
};

const firstStopOverCircle3 = {
  "circle-radius": 12,
  "circle-fill-color": ["case", ["==", ["get", "type"], "hiking"], selfDrawnHikingColor, stopOverColor],
};

const firstStopOver = [controlPointBackground, firstStopOverCircle3, firstStopOverCircle2, firstStopOverCircle1];

const lastStopOverCircle1 = {
  "circle-radius": 6,
  "circle-fill-color": "#fff",
};

const lastStopOverCircle2 = {
  "circle-radius": 12,
  "circle-fill-color": ["case", ["==", ["get", "type"], "hiking"], selfDrawnHikingColor, stopOverColor],
};

const lastStopOver = [controlPointBackground, lastStopOverCircle2, lastStopOverCircle1];

/**
 * Track Style
 */

const trackLineBackground = {
  "stroke-color": "#fff",
  "stroke-width": 10,
};

const trackLineForeground = {
  "stroke-color": ["case", ["==", ["get", "type"], "hiking"], selfDrawnHikingColor, tourColor],
  "stroke-width": 7,
};

const trackLine = [trackLineBackground, trackLineForeground];

// cluster of multiple points
const isCluster = ["all", ["get", "uuids_count"], ["!=", ["get", "uuids_count"], "1"]];
// single point
const isSinglePoint = ["==", ["get", "uuids_count"], "1"];

/**
 * Text Style
 */

const textStyle = {
  "text-value": ["case", isCluster, ["get", "uuids_count"], ""],
  "text-font": "600 12px 'Montserrat'",
  "text-fill-color": darkGrey,
  "text-offset-y": 1,
  "text-stroke-color": "white",
  "text-stroke-width": 2,
};

/**
 * POI Style
 */

// TODO:
const poiIconStyle = {
  "icon-scale": 0.15,
  "icon-anchor": [0.5, 1],
};

const poiClusterStyle = {
  ...poiIconStyle,
  ...textStyle,
  "text-offset-y": -20,
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(poiClusterIcon)}`,
};

const poiRestaurantStyle = {
  ...poiIconStyle,
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(poiRestaurantIcon)}`,
};

const poiHotelStyle = {
  ...poiIconStyle,
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(poiHotelIcon)}`,
};

const poiFireplaceStyle = {
  ...poiIconStyle,
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(poiFireplaceIcon)}`,
};

const poiVideoStyle = {
  ...poiIconStyle,
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(poiVideoIcon)}`,
};

export const poiStyle = [
  {
    filter: isCluster,
    style: poiClusterStyle,
  },
  {
    filter: [
      "any",
      ["all", isSinglePoint, ["==", ["get", "types"], "hiking restaurant"]],
      ["==", ["get", "poi_type"], "hiking restaurant"],
    ],
    style: poiRestaurantStyle,
  },
  {
    filter: [
      "any",
      ["all", isSinglePoint, ["==", ["get", "types"], "hiking hotels"]],
      ["==", ["get", "poi_type"], "hiking hotels"],
    ],
    style: poiHotelStyle,
  },
  {
    filter: [
      "any",
      ["all", isSinglePoint, ["==", ["get", "types"], "fireplaces"]],
      ["==", ["get", "poi_type"], "fireplaces"],
    ],
    style: poiFireplaceStyle,
  },
  {
    filter: [
      "any",
      ["all", isSinglePoint, ["==", ["get", "types"], "hiking videos"]],
      ["==", ["get", "poi_type"], "hiking videos"],
    ],
    style: poiVideoStyle,
  },
];

/**
 * Hiking (self drawn) style
 */

export const hikingStyle = [
  {
    filter: isCluster,
    style: {
      "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(hikingClusterIcon)}`,
      "icon-scale": 0.15,
      ...textStyle,
    },
  },
  {
    filter: isSinglePoint,
    style: {
      "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(hikingIcon)}`,
      "icon-scale": 0.15,
    },
  },
  {
    filter: ["==", ["get", "layer"], "hiking_lines"],
    style: trackLine,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "middle"]],
    style: stopOver,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "first"]],
    style: firstStopOver,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "last"]],
    style: lastStopOver,
  },
];

/**
 * Hiking Activity
 */

export const hikingActivityStyle = [
  {
    filter: isCluster,
    style: {
      "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(hikingActivityClusterIcon)}`,
      "icon-scale": 0.15,
      ...textStyle,
    },
  },
  {
    filter: isSinglePoint,
    style: {
      "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(hikingActivityIcon)}`,
      "icon-scale": 0.15,
    },
  },
  {
    // special case for hiking activity: if no geometry is present, we display the starting point
    filter: ["==", ["get", "layer"], "hiking_activity_lines"],
    style: {
      "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(hikingActivityIcon)}`,
      "icon-scale": 0.15,
    },
  },
  {
    filter: ["==", ["get", "layer"], "hiking_activity_lines"],
    style: trackLine,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "middle"]],
    style: stopOver,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "first"]],
    style: firstStopOver,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "last"]],
    style: lastStopOver,
  },
];

/**
 * Hiking Proposition
 */

const alpineHikeStyle = {
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(alpineHikeIcon)}`,
  "icon-scale": 1.2,
  ...textStyle,
};

const mountainHikeStyle = {
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(mountainHikeIcon)}`,
  "icon-scale": 1.2,
  ...textStyle,
};

const snowShoeHikeStyle = {
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(snowShoeHikeIcon)}`,
  "icon-scale": 1.5,
};

const hikeStyle = {
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(hikeIcon)}`,
  "icon-scale": 1.5,
  ...textStyle,
};

const winterHikeStyle = {
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(winterHikeIcon)}`,
  "icon-scale": 1.5,
  ...textStyle,
};

const schooltripHikeStyle = {
  "icon-src": `data:image/svg+xml;utf8, ${encodeURIComponent(schooltripHikeIcon)}`,
  "icon-scale": 0.15,
};

// Hiking Proposition Style
// cluster category is randomly set on the server side
// for [alpine hike, mountain hike, hike, winter hike] use the corresponding icon for cluster and single point
// the other categories, use the hike icon for cluster and corresponding icon for single point
export const hikingPropositionStyle = [
  {
    filter: ["==", ["get", "cluster_category"], "alpine hike"],
    style: alpineHikeStyle,
  },
  {
    filter: ["==", ["get", "cluster_category"], "mountain hike"],
    style: mountainHikeStyle,
  },
  {
    filter: ["==", ["get", "cluster_category"], "hike"],
    style: hikeStyle,
  },
  {
    filter: ["==", ["get", "cluster_category"], "winter hike"],
    style: winterHikeStyle,
  },
  {
    filter: ["all", isSinglePoint, ["==", ["get", "cluster_category"], "snow shoe hike"]],
    style: snowShoeHikeStyle,
  },
  {
    filter: [
      "all",
      isSinglePoint,
      [
        "any",
        ["==", ["get", "cluster_category"], "schooltrip with hike"],
        ["==", ["get", "cluster_category"], "schooltrip without hike"],
      ],
    ],
    style: schooltripHikeStyle,
  },
  {
    filter: [
      "all",
      isCluster,
      [
        "any",
        ["==", ["get", "cluster_category"], "snow shoe hike"],
        ["==", ["get", "cluster_category"], "schooltrip with hike"],
        ["==", ["get", "cluster_category"], "schooltrip without hike"],
      ],
    ],
    style: hikeStyle,
  },
  {
    filter: ["all", isSinglePoint, ["!", ["get", "cluster_category"]]],
    style: hikeStyle,
  },
  {
    filter: ["==", ["get", "layer"], "hiking_proposition_lines"],
    style: trackLine,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "middle"]],
    style: stopOver,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "first"]],
    style: firstStopOver,
  },
  {
    filter: ["all", ["==", ["get", "layer"], "stop_over"], ["==", ["get", "stopover_position"], "last"]],
    style: lastStopOver,
  },
];

export const detourStyle = [
  {
    filter: ["==", ["get", "layer"], "detour_points"],
    style: detourStartEndPoint,
  },
  {
    filter: ["==", ["get", "layer"], "detour_line"],
    style: detourLine,
  },
  {
    filter: ["==", ["get", "layer"], "detour_closed_way"],
    style: detourClosedWay,
  },
];
